import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/BrandLogo';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-border">
        <div className="landing-container h-16 flex items-center justify-between">
          <Link to="/"><BrandLogo size="sm" /></Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            &larr; Back
          </Link>
        </div>
      </nav>

      {/* Content */}
      <article className="landing-container py-16 md:py-24 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Last updated: March 2026
        </p>

        {/* 1 */}
        <Section title="1. Introduction">
          <P>
            Agentive Property (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) operates the
            Agentive Property platform accessible at agentiveproperty.ai (the &ldquo;Platform&rdquo;).
            We are committed to protecting the privacy of your personal information in accordance with
            the Privacy Act 1988 (Cth) and the Australian Privacy Principles (&ldquo;APPs&rdquo;).
          </P>
          <P>
            This Privacy Policy explains how we collect, hold, use, and disclose your personal
            information, and how you can access or correct it, or make a complaint about our handling
            of your information.
          </P>
        </Section>

        {/* 2 */}
        <Section title="2. Information We Collect">
          <P>We may collect the following types of personal information:</P>
          <List items={[
            'Identity information: full name, date of birth, business name, ABN/ACN',
            'Contact information: email address, phone number, postal address',
            'Property information: property addresses, tenancy details, maintenance history',
            'Financial information: billing details processed securely via Stripe (we do not store full credit card numbers)',
            'Professional information: trade licences, insurance details, qualifications (for tradies)',
            'Content you provide: maintenance request descriptions, photographs, messages, quotes, reviews',
            'Technical information: IP address, browser type, device information, operating system',
            'Usage information: pages visited, features used, timestamps, interaction patterns',
          ]} />
        </Section>

        {/* 3 */}
        <Section title="3. How We Collect Information">
          <Sub title="Directly from you">
            <P>
              We collect information you provide when you create an account, submit a maintenance
              request, provide a quote, complete forms, or communicate with us or other users through
              the Platform.
            </P>
          </Sub>
          <Sub title="Automatically">
            <P>
              We automatically collect technical and usage information when you use the Platform,
              including through cookies, analytics tools, and server logs.
            </P>
          </Sub>
          <Sub title="From third parties">
            <P>
              We may receive information from third-party service providers such as identity
              verification services, payment processors, or publicly available sources such as
              trade licence registers.
            </P>
          </Sub>
        </Section>

        {/* 4 */}
        <Section title="4. Purpose of Collection">
          <P>We collect and use your personal information for the following purposes:</P>
          <List items={[
            'Providing and operating the Platform, including account management and authentication',
            'Facilitating maintenance requests, AI-powered estimates, tradie quotes, and landlord approvals',
            'Processing payments and managing billing through our payment provider (Stripe)',
            'Communicating with you about your account, requests, quotes, and platform updates',
            'Verifying tradie licences, insurance, and professional qualifications',
            'Improving the Platform, including analysing usage patterns and developing new features',
            'Complying with legal obligations, including tax, licensing, and regulatory requirements',
            'Preventing fraud, enforcing our terms of service, and protecting the safety of users',
          ]} />
        </Section>

        {/* 5 */}
        <Section title="5. AI and Automated Processing">
          <P>
            Our Platform uses artificial intelligence to analyse maintenance request descriptions
            and photographs to provide indicative cost estimates, classify the type of trade
            required, and assess urgency levels. This processing is designed to assist property
            managers and is not a substitute for professional assessment.
          </P>
          <P>
            AI-generated estimates are indicative only and are always supplemented by quotes from
            qualified tradies. No significant decisions affecting your rights are made solely by
            automated means without human review.
          </P>
        </Section>

        {/* 6 */}
        <Section title="6. Disclosure to Third Parties">
          <P>We may disclose your personal information to:</P>
          <List items={[
            'Other users of the Platform as necessary for service delivery (e.g., property managers see tradie quotes; tradies see job details and property addresses)',
            'Infrastructure and service providers: Supabase (database and authentication), Stripe (payment processing), Resend (transactional emails), Google (AI processing)',
            'Professional advisers: lawyers, accountants, auditors, and insurers',
            'Government authorities: where required by law, regulation, or court order',
          ]} />
          <P>
            We do not sell, rent, or trade your personal information to third parties for marketing
            purposes.
          </P>
        </Section>

        {/* 7 */}
        <Section title="7. Cross-Border Disclosure">
          <P>
            In accordance with APP 8, we disclose that your personal information may be processed
            by service providers located outside Australia, including in the United States and the
            European Union. Our key service providers and their locations include:
          </P>
          <List items={[
            'Supabase (United States) — database hosting and authentication',
            'Stripe (United States) — payment processing',
            'Resend / Amazon SES (United States) — email delivery',
            'Google Cloud (United States / global) — AI processing',
          ]} />
          <P>
            We take reasonable steps to ensure that overseas recipients handle your personal
            information in accordance with the APPs. Where practical, we use data processing
            agreements and select providers with appropriate security certifications.
          </P>
        </Section>

        {/* 8 */}
        <Section title="8. Data Security">
          <P>
            We take reasonable steps to protect your personal information from misuse, interference,
            loss, unauthorised access, modification, or disclosure. Our security measures include:
          </P>
          <List items={[
            'Encryption of data in transit (TLS/SSL) and at rest',
            'Row-level security (RLS) policies ensuring users can only access their own data',
            'Secure authentication with hashed passwords and session management',
            'Regular security reviews and monitoring of access logs',
            'Restricting access to personal information to authorised personnel only',
          ]} />
          <P>
            No method of electronic storage or transmission is completely secure. While we strive to
            use commercially acceptable means to protect your information, we cannot guarantee
            absolute security.
          </P>
        </Section>

        {/* 9 */}
        <Section title="9. Data Retention and Deletion">
          <P>
            We retain your personal information only for as long as necessary to fulfil the purposes
            for which it was collected, or as required by law. When personal information is no longer
            needed, we will take reasonable steps to destroy or de-identify it in accordance with
            APP 11.2.
          </P>
          <P>
            You may request deletion of your account and associated personal information by
            contacting us at{' '}
            <a href="mailto:privacy@agentiveproperty.ai" className="text-accent hover:underline">
              privacy@agentiveproperty.ai
            </a>
            . Some information may be retained where required by law (e.g., financial records for
            tax purposes) or where necessary to resolve disputes.
          </P>
        </Section>

        {/* 10 */}
        <Section title="10. Access and Correction">
          <P>
            Under APPs 12 and 13, you have the right to request access to the personal information
            we hold about you, and to request correction of any information that is inaccurate,
            out-of-date, incomplete, or misleading.
          </P>
          <P>
            You can access and update much of your information directly through your account
            settings on the Platform. For other access or correction requests, please contact us
            at{' '}
            <a href="mailto:privacy@agentiveproperty.ai" className="text-accent hover:underline">
              privacy@agentiveproperty.ai
            </a>
            . We will respond to requests within 30 days. We may refuse access in certain
            circumstances permitted by the Privacy Act, and will provide reasons if we do so.
          </P>
        </Section>

        {/* 11 */}
        <Section title="11. Cookies and Tracking">
          <P>
            The Platform uses cookies and similar technologies to maintain your session, remember
            your preferences, and understand how you use the Platform. We use:
          </P>
          <List items={[
            'Essential cookies: required for authentication and core platform functionality',
            'Analytics cookies: to understand usage patterns and improve the Platform',
          ]} />
          <P>
            You can manage cookie preferences through your browser settings. Disabling essential
            cookies may prevent you from using certain features of the Platform.
          </P>
        </Section>

        {/* 12 */}
        <Section title="12. Notifiable Data Breaches">
          <P>
            In accordance with the Notifiable Data Breaches (NDB) scheme under Part IIIC of the
            Privacy Act 1988, if we become aware of a data breach that is likely to result in
            serious harm to any individual whose personal information is involved, we will:
          </P>
          <List items={[
            'Take immediate steps to contain the breach and mitigate potential harm',
            'Assess the breach to determine whether it is likely to result in serious harm',
            'Notify the Office of the Australian Information Commissioner (OAIC) and affected individuals as soon as practicable',
            'Provide recommendations about steps individuals can take to protect themselves',
          ]} />
        </Section>

        {/* 13 */}
        <Section title="13. Children's Privacy">
          <P>
            The Platform is not intended for use by individuals under the age of 18. We do not
            knowingly collect personal information from children. If we become aware that we have
            collected personal information from a child under 18, we will take steps to delete that
            information promptly.
          </P>
        </Section>

        {/* 14 */}
        <Section title="14. Changes to This Policy">
          <P>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, or legal requirements. We will notify you of material changes by posting the
            updated policy on the Platform with a revised &ldquo;Last updated&rdquo; date. We
            encourage you to review this policy periodically.
          </P>
        </Section>

        {/* 15 */}
        <Section title="15. Contact Us and Complaints">
          <P>
            If you have questions about this Privacy Policy, wish to make an access or correction
            request, or have a complaint about how we have handled your personal information, please
            contact us:
          </P>
          <P>
            <strong className="text-foreground">Agentive Property</strong>
            <br />
            Email:{' '}
            <a href="mailto:privacy@agentiveproperty.ai" className="text-accent hover:underline">
              privacy@agentiveproperty.ai
            </a>
          </P>
          <P>
            We will acknowledge your complaint within 7 days and aim to resolve it within 30 days.
            If you are not satisfied with our response, you may lodge a complaint with the Office of
            the Australian Information Commissioner (OAIC) at{' '}
            <a
              href="https://www.oaic.gov.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              www.oaic.gov.au
            </a>{' '}
            or by calling 1300 363 992.
          </P>
        </Section>
      </article>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="landing-container py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">&larr; Back to Home</Link>
          <span>&copy; {new Date().getFullYear()} Agentive Property</span>
        </div>
      </footer>
    </div>
  );
};

/* ─── Reusable typography components ────────────────────────────────────── */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-12 first:mt-0">
    <h2 className="text-xl font-semibold text-foreground mb-4">{title}</h2>
    {children}
  </section>
);

const Sub: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-base font-medium text-foreground mb-2">{title}</h3>
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-base text-muted-foreground leading-relaxed mb-4">{children}</p>
);

const List: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 text-base text-muted-foreground leading-relaxed mb-4">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default PrivacyPolicy;
